import { useState, useEffect } from "react";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { AlertTriangle, Search, Calendar, User, BookOpen, Download } from "lucide-react";
import { circulationApi, type CheckoutRecord, type PaginatedResponse } from "@/lib/api/circulation";

const OverduePage = () => {
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<CheckoutRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadOverdue();
  }, [page]);

  const loadOverdue = async () => {
    setLoading(true);
    try {
      const result = await circulationApi.getOverdue({ page, limit: 30 });
      setData(result);
    } catch {
      toast.error("Failed to load overdue items");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const result = await circulationApi.exportOverdue();
      const rows = (result.data as CheckoutRecord[]).map((co) => {
        const days = getDaysOverdue(co.dueDate);
        return [
          co.item?.biblio?.title ?? co.book?.title ?? "Unknown",
          co.item?.barcode ?? "",
          co.item?.accessionNumber ?? "",
          `${co.user?.firstName ?? ""} ${co.user?.lastName ?? ""}`.trim(),
          co.user?.email ?? "",
          new Date(co.dueDate).toLocaleDateString(),
          days,
          co.checkoutBranch?.name ?? "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",");
      });
      const csv = [
        "Title,Barcode,Accession#,Patron,Email,Due Date,Days Overdue,Branch",
        ...rows,
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `overdue-items-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${result.data.length} records`);
    } catch {
      toast.error("Failed to export overdue list");
    } finally {
      setExporting(false);
    }
  };

  const getDaysOverdue = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diff = Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const filteredData = data?.data?.filter((co) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (co.item?.biblio?.title ?? co.book?.title ?? "").toLowerCase().includes(q) ||
      co.user?.firstName?.toLowerCase().includes(q) ||
      co.user?.lastName?.toLowerCase().includes(q) ||
      co.user?.email?.toLowerCase().includes(q) ||
      co.item?.barcode?.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Overdue Management"
          description={data ? `${data.total} overdue item(s)` : "Loading..."}
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Circulation", href: "/circulation" },
            { label: "Overdue" },
          ]}
          showSearch={false}
          titleSize="md"
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exporting || !data?.total}
            >
              <Download className="w-4 h-4 mr-1.5" />
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          }
        />

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by title, patron, barcode..."
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No overdue items</p>
            <p className="text-sm">All items are returned on time.</p>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Item</th>
                    <th className="px-4 py-3 font-medium">Patron</th>
                    <th className="px-4 py-3 font-medium">Due Date</th>
                    <th className="px-4 py-3 font-medium">Days Overdue</th>
                    <th className="px-4 py-3 font-medium">Branch</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredData.map((co) => {
                    const days = getDaysOverdue(co.dueDate);
                    return (
                      <tr key={co.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium truncate max-w-[250px]">
                                {co.item?.biblio?.title ?? co.book?.title ?? "Unknown"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {co.item?.barcode ? `${co.item.barcode}` : ""}
                                {co.item?.accessionNumber
                                  ? ` | Acc#${co.item.accessionNumber}`
                                  : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <p className="truncate">
                                {co.user?.firstName} {co.user?.lastName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {co.user?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                            {new Date(co.dueDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="destructive"
                            className={`text-xs ${
                              days > 14 ? "bg-red-600" : days > 7 ? "bg-red-500" : "bg-red-400"
                            }`}
                          >
                            {days} day{days !== 1 ? "s" : ""}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {co.checkoutBranch?.name ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default OverduePage;
